import type { DropdownMenuItem } from '@timelyapp/react';
import { Button, DropdownMenu } from '@timelyapp/react';
import classNames from 'classnames';
import { Ellipsis } from 'components/Icons';
import Surface from 'components/Surface';
import React, { useCallback } from 'react';
import styles from './index.module.scss';

interface CardProps<TRef> {
  dropdownItems?: DropdownMenuItem[];
  clickReference?: TRef;
  onClick?: (ref: TRef) => void;
  children?: React.ReactNode;
  className?: string;
  isActive?: boolean;
  variant?: 'default' | 'raised';
  optionsIconClassName?: string;
}

/**
 * A modern card component that uses DropdownMenu for dropdown functionality
 */
export function Card<TRef extends {} | number>({
  dropdownItems = [],
  clickReference,
  onClick,
  children,
  className,
  variant = 'default',
  isActive,
  optionsIconClassName,
  ...rest
}: CardProps<TRef>) {
  const handleCardClick = useCallback(() => {
    if (onClick && clickReference) {
      onClick(clickReference);
    }
  }, [onClick, clickReference]);

  const isClickable = !!onClick;
  const containerClass = classNames(styles.card, className, styles[`variant-${variant}`], {
    [styles.clickable]: isClickable,
    [styles.active]: isActive,
  });

  const showOptions = dropdownItems.length > 0;

  return (
    <Surface
      surface={1}
      className={containerClass}
      onClick={isClickable ? handleCardClick : undefined}
      {...rest}
    >
      {showOptions && (
        <div
          className={styles.optionsDropdown}
          onClick={e => e.stopPropagation()} // Prevent click from bubbling up to card
        >
          <DropdownMenu
            closeOnSelect
            trigger={
              <Button className={styles.dotdotdot} priority="low">
                <Ellipsis className={optionsIconClassName} />
              </Button>
            }
            items={dropdownItems}
          />
        </div>
      )}
      {children}
    </Surface>
  );
}
